import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { confirmer } from '../components/Confirm';
import {
  api, downloadPdf,
  type CatalogueHit, type ClientDetail, type ClientsPage, type PriceRuleRow,
  type PricelistAdmin, type RulePayload,
} from '../api';
import Pager from '../components/Pager';
import { SearchInput, SortTh, useSort } from '../components/ListSort';

const PAGE_SIZE = 25;

function fmtMoney(n: number): string {
  return n.toLocaleString('fr-CH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
const LANGUES: Record<string, string> = { fr_FR: 'Français', en_US: 'Anglais' };
function langue(code: string, langs?: { code: string; name: string }[]): string {
  return LANGUES[code] ?? langs?.find((l) => l.code === code)?.name ?? code ?? '—';
}

/**
 * Écran Clients (ticket #26) : coordonnées et langue, liste de prix appliquée,
 * remises et prix négociés, PDF du catalogue tarifé à envoyer au client.
 *
 * Deux choses que l'écran doit dire, parce qu'elles ne se devinent pas :
 *  — une **liste partagée** ne peut pas porter de remise (elle vaudrait pour tous
 *    les clients qui l'utilisent) ; l'app propose alors d'en créer une dédiée ;
 *  — dès qu'une **majoration** existe, le PDF masque les prix catalogue sur
 *    TOUTES les lignes. Autant le savoir avant de l'envoyer.
 */
export default function Clients() {
  const [data, setData] = useState<ClientsPage | null>(null);
  const [offset, setOffset] = useState(0);
  // `loading` = premier chargement (il n'y a rien à montrer) ; `refreshing` =
  // on a déjà des lignes à l'écran et on les remplace. Confondre les deux faisait
  // disparaître la liste ET la fiche ouverte après CHAQUE action : à l'usage, ça
  // se lit comme un rechargement de page.
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Recherche, filtre et fiche ouverte vivent dans l'URL : l'écran reste
  // atteignable par lien (`/clients?client=42`) et « précédent » garde du sens.
  const [params, setParams] = useSearchParams();
  const q = params.get('q') ?? '';
  const liste = params.get('liste') ?? 'tout';
  const openId = Number(params.get('client')) || null;
  const { sort, dir, toggle } = useSort('nom', 'asc');
  const [gestion, setGestion] = useState(false);

  function patch(next: Record<string, string | null>) {
    const p = new URLSearchParams(params);
    for (const [k, v] of Object.entries(next)) { if (v) p.set(k, v); else p.delete(k); }
    setParams(p, { replace: true });
  }
  const setOpenId = (id: number | null) => patch({ client: id ? String(id) : null });

  const reload = useCallback(() => {
    setRefreshing(true);
    setError(null);
    api.getClients({ q, sort, dir, liste, limit: PAGE_SIZE, offset })
      .then(setData)
      .catch((e) => setError(e.message))
      .finally(() => setRefreshing(false));
  }, [q, sort, dir, liste, offset]);
  useEffect(() => { reload(); }, [reload]);
  const loading = refreshing && data == null;

  const listes = data?.pricelists ?? [];

  return (
    <div className="devis-page">
      <div className="page-toolbar">
        <button className="devis-refresh" onClick={reload} disabled={refreshing}>
          {refreshing ? '↻ Actualisation…' : '↻ Rafraîchir'}
        </button>
        <button className="devis-refresh" onClick={() => setGestion((v) => !v)}>
          {gestion ? 'Masquer les listes de prix' : 'Gérer les listes de prix'}
        </button>
      </div>

      {gestion && <GestionListes onChanged={reload} />}

      <div className="ventes-toolbar">
        <div className="suivi-filters">
          <button
            className={`suivi-filter ${liste === 'tout' ? 'active' : ''}`}
            onClick={() => { patch({ liste: null, client: null }); setOffset(0); }}
          >Toutes les listes{data ? ` (${data.counts.total})` : ''}</button>
          {listes.map((l) => (
            <button
              key={l.id}
              className={`suivi-filter ${liste === String(l.id) ? 'active' : ''}`}
              onClick={() => { patch({ liste: String(l.id), client: null }); setOffset(0); }}
              title={`${l.name} — ${l.currency}`}
            >{l.name} ({l.clients})</button>
          ))}
        </div>
        <SearchInput
          value={q}
          onChange={(v) => { patch({ q: v || null, client: null }); setOffset(0); }}
          placeholder="Rechercher (nom, email, ville, TVA)…"
        />
      </div>

      <div className="devis-list">
        {loading && <div className="devis-empty">Chargement…</div>}
        {error && <div className="devis-empty err">Erreur : {error}</div>}
        {!loading && data && data.items.length === 0 && <div className="devis-empty">Aucun client.</div>}
        {data && data.items.length > 0 && (
          <table>
            <thead>
              <tr>
                <SortTh col="nom" label="Client" sort={sort} dir={dir} onSort={toggle} />
                <SortTh col="ville" label="Ville" sort={sort} dir={dir} onSort={toggle} className="sm-hide" />
                <SortTh col="pays" label="Pays" sort={sort} dir={dir} onSort={toggle} className="sm-hide" />
                <SortTh col="langue" label="Langue" sort={sort} dir={dir} onSort={toggle} className="sm-hide" />
                <SortTh col="liste" label="Liste de prix" sort={sort} dir={dir} onSort={toggle} />
              </tr>
            </thead>
            <tbody>
              {data.items.flatMap((c) => {
                const row = (
                  <tr
                    key={c.id}
                    className={openId === c.id ? 'row-selected' : ''}
                    title="Ouvrir la fiche client"
                    onClick={() => setOpenId(openId === c.id ? null : c.id)}
                  >
                    <td className="cell-title"><span className="link-btn">{c.name}</span></td>
                    <td className="sm-hide" data-label="Ville">{c.city || '—'}</td>
                    <td className="sm-hide" data-label="Pays">{c.country || '—'}</td>
                    <td className="sm-hide" data-label="Langue">{langue(c.lang)}</td>
                    <td data-label="Liste de prix">{c.pricelist?.name ?? '—'}</td>
                  </tr>
                );
                if (openId !== c.id) return [row];
                return [row, (
                  <tr key={`${c.id}-detail`} className="detail-row">
                    <td colSpan={5}><ClientPanel id={c.id} onClose={() => setOpenId(null)} onSaved={reload} /></td>
                  </tr>
                )];
              })}
            </tbody>
          </table>
        )}
        {data && (
          <Pager
            total={data.total} limit={data.limit} offset={data.offset}
            onOffset={(o) => { setOffset(o); setOpenId(null); }}
            unit={['client', 'clients']}
            capped={data.counts.capped}
          />
        )}
      </div>
    </div>
  );
}

// ---------- Listes de prix ----------

/**
 * Inventaire des listes et retrait de celles qui ne servent plus. Une liste
 * encore assignée à un client n'est PAS supprimable : le retirer basculerait ce
 * client sur un tarif qu'il n'a pas négocié. Une liste citée par des ventes est
 * archivée plutôt que supprimée, pour que les documents passés gardent le tarif
 * sous lequel ils ont été établis.
 */
function GestionListes({ onChanged }: { onChanged: () => void }) {
  const [items, setItems] = useState<PricelistAdmin[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [flash, setFlash] = useState<string | null>(null);
  const [busy, setBusy] = useState<number | null>(null);

  const load = useCallback(() => {
    api.getPricelists().then((r) => setItems(r.items)).catch((e) => setError(e.message));
  }, []);
  useEffect(() => { load(); }, [load]);

  async function retirer(l: PricelistAdmin) {
    if (!await confirmer(`Retirer la liste « ${l.name} » ?`)) return;
    setBusy(l.id); setError(null); setFlash(null);
    try {
      const r = await api.deletePricelist(l.id);
      setFlash(r.archived
        ? `« ${l.name} » archivée : ${r.orders} vente(s) y font référence, leurs prix sont préservés.`
        : `« ${l.name} » supprimée.`);
      load(); onChanged();
    } catch (e: any) {
      setError(String(e.message ?? e).replace(/^\d+\s*/, ''));
    } finally { setBusy(null); }
  }

  return (
    <div className="devis-list" style={{ marginBottom: 14, padding: '14px 16px' }}>
      <h4 style={{ margin: '0 0 8px' }}>Listes de prix</h4>
      {error && <div className="devis-empty err">{error}</div>}
      {flash && <div className="devis-empty">{flash}</div>}
      {!items ? <div className="devis-empty">Chargement…</div> : (
        <table>
          <thead>
            <tr>
              <th>Liste</th><th>Devise</th>
              <th className="num">Clients</th><th className="num">Règles</th><th className="num">Ventes</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {items.map((l) => (
              <tr key={l.id}>
                <td className="cell-title">{l.name}</td>
                <td>{l.currency}</td>
                <td className="num">{l.clients}</td>
                <td className="num">{l.rules}</td>
                <td className="num">{l.orders}</td>
                <td>
                  <button
                    className="devis-refresh"
                    disabled={l.clients > 0 || busy === l.id}
                    title={l.clients > 0
                      ? `Encore utilisée par ${l.clients} client(s) : affectez-leur une autre liste d'abord`
                      : l.orders > 0 ? `${l.orders} vente(s) y font référence : elle sera archivée, pas supprimée`
                        : 'Supprimer définitivement'}
                    onClick={() => retirer(l)}
                  >{l.orders > 0 ? 'Archiver' : 'Supprimer'}</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

// ---------- Fiche client ----------

function ClientPanel({ id, onClose, onSaved }: { id: number; onClose: () => void; onSaved: () => void }) {
  const [d, setD] = useState<ClientDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [flash, setFlash] = useState<string | null>(null);

  // Pas d'état « chargement » ici : après une action, la fiche est relue en
  // arrière-plan et reste lisible. La seule attente visible est la première.
  const load = useCallback(() => {
    api.getClient(id).then(setD).catch((e) => setError(e.message));
  }, [id]);
  useEffect(() => { load(); }, [load]);

  async function run(label: string, fn: () => Promise<unknown>, note: string) {
    setBusy(label); setError(null); setFlash(null);
    try { await fn(); setFlash(note); load(); onSaved(); }
    catch (e: any) { setError(String(e.message ?? e).replace(/^\d+\s*/, '')); }
    finally { setBusy(null); }
  }

  if (error && !d) return <div className="devis-empty err">Erreur : {error}</div>;
  if (!d) return <div className="devis-empty">Chargement de la fiche…</div>;

  return (
    <div style={{ padding: '16px 18px' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, flexWrap: 'wrap' }}>
        <h3 style={{ margin: 0 }}>{d.client.name}</h3>
        {d.pricelist && <span className="badge">{d.pricelist.name} · {d.pricelist.currency}</span>}
        <button
          className="devis-refresh"
          disabled={!d.pricelist || busy === 'pdf'}
          title={d.pricelist ? 'Catalogue complet aux prix de ce client' : "Ce client n'a aucune liste de prix"}
          onClick={() => run('pdf', () => downloadPdf(`/api/clients/${id}/pricelist.pdf`), 'PDF téléchargé.')}
        >⬇ Liste de prix (PDF)</button>
        <button className="devis-refresh" style={{ marginLeft: 'auto' }} onClick={onClose}>Fermer</button>
      </div>

      {error && <div className="devis-empty err" style={{ marginTop: 10 }}>{error}</div>}
      {flash && <div className="devis-empty" style={{ marginTop: 10 }}>{flash}</div>}

      {/* Une majoration ne doit jamais se lire sur le document du client : le PDF
          retire alors le prix catalogue de TOUTES les lignes, pas seulement des
          lignes majorées — une colonne vide par endroits se lirait tout autant. */}
      {!d.shows_discounts && (
        <div className="devis-empty" style={{ marginTop: 10 }}>
          ⚠️ Ce client a une majoration : le PDF n'affichera que le prix final, sans prix catalogue
          ni remise, pour ne rien laisser deviner de l'augmentation.
        </div>
      )}

      <Coordonnees key={d.client.id} d={d} busy={busy} onSave={(patch) => run('infos', () => api.updateClient(id, patch), 'Fiche enregistrée.')} />
      <ListeDePrix key={d.pricelist?.id ?? 0} d={d} busy={busy} onSet={(body) => run('liste', () => api.setClientPricelist(id, body), 'Liste de prix mise à jour.')} />
      <Regles
        d={d} busy={busy}
        onAdd={(body) => run('regle', () => api.addPriceRule(id, body), 'Règle ajoutée.')}
        onDelete={(ruleId) => run('regle', () => api.deletePriceRule(id, ruleId), 'Règle supprimée.')}
      />
    </div>
  );
}

function Coordonnees({ d, busy, onSave }: {
  d: ClientDetail; busy: string | null; onSave: (p: Record<string, unknown>) => void;
}) {
  const c = d.client;
  // L'état part de la fiche lue ; `key` sur l'id du client remonte le formulaire
  // quand on passe d'une fiche à l'autre.
  const [f, setF] = useState({
    name: c.name, street: c.street, street2: c.street2, zip: c.zip, city: c.city,
    phone: c.phone, email: c.email, vat: c.vat, lang: c.lang,
    country_id: c.country?.id ?? 0,
  });
  const dirty = useMemo(() => (
    f.name !== c.name || f.street !== c.street || f.street2 !== c.street2 || f.zip !== c.zip
    || f.city !== c.city || f.phone !== c.phone || f.email !== c.email || f.vat !== c.vat
    || f.lang !== c.lang || f.country_id !== (c.country?.id ?? 0)
  ), [f, c]);

  const champ = (k: keyof typeof f, label: string, type = 'text') => (
    <label className="form-field">
      <span>{label}</span>
      <input
        type={type} value={String(f[k] ?? '')}
        onChange={(e) => setF((s) => ({ ...s, [k]: e.target.value }))}
      />
    </label>
  );

  return (
    <section style={{ marginTop: 18 }}>
      <h4 style={{ margin: '0 0 8px' }}>Coordonnées</h4>
      <div className="form-grid">
        {champ('name', 'Nom')}
        {champ('email', 'Email', 'email')}
        {champ('phone', 'Téléphone')}
        {champ('street', 'Adresse')}
        {champ('street2', 'Complément')}
        {champ('zip', 'NPA')}
        {champ('city', 'Ville')}
        <label className="form-field">
          <span>Pays</span>
          <select value={f.country_id} onChange={(e) => setF((s) => ({ ...s, country_id: Number(e.target.value) }))}>
            <option value={0}>—</option>
            {d.countries.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </label>
        {champ('vat', 'N° TVA')}
        <label className="form-field">
          <span>Langue</span>
          {/* La langue décide de celle des documents envoyés par Odoo. */}
          <select value={f.lang} onChange={(e) => setF((s) => ({ ...s, lang: e.target.value }))}>
            {d.langs.map((l) => <option key={l.code} value={l.code}>{langue(l.code, d.langs)}</option>)}
          </select>
        </label>
      </div>
      <button
        className="devis-action" style={{ marginTop: 10 }}
        disabled={!dirty || busy === 'infos'}
        onClick={() => onSave({ ...f, country_id: f.country_id || null })}
      >{busy === 'infos' ? 'Enregistrement…' : 'Enregistrer'}</button>
    </section>
  );
}

function ListeDePrix({ d, busy, onSet }: {
  d: ClientDetail; busy: string | null; onSet: (b: { pricelist_id?: number; create?: boolean }) => void;
}) {
  const [choix, setChoix] = useState<number>(d.pricelist?.id ?? 0);
  return (
    <section style={{ marginTop: 18 }}>
      <h4 style={{ margin: '0 0 8px' }}>Liste de prix</h4>
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
        <select value={choix} onChange={(e) => setChoix(Number(e.target.value))}>
          <option value={0}>—</option>
          {d.pricelists.map((l) => (
            <option key={l.id} value={l.id}>{l.name} · {l.currency} ({l.clients} client{l.clients > 1 ? 's' : ''})</option>
          ))}
        </select>
        <button
          className="devis-action"
          disabled={!choix || choix === d.pricelist?.id || busy === 'liste'}
          onClick={() => onSet({ pricelist_id: choix })}
        >Appliquer</button>
        <button
          className="devis-refresh"
          disabled={busy === 'liste'}
          title="Crée une liste au nom de ce client, dans la devise de sa liste actuelle"
          onClick={() => onSet({ create: true })}
        >+ Liste dédiée à ce client</button>
      </div>
      {/* Poser une remise sur une liste partagée la donnerait à tous ses clients :
          l'écran le dit ici plutôt que de laisser le serveur refuser plus tard. */}
      {d.pricelist?.shared && (
        <div style={{ marginTop: 8, opacity: .8 }}>
          « {d.pricelist.name} » sert à {d.pricelist.clients} client{d.pricelist.clients > 1 ? 's' : ''} :
          aucune remise ne peut y être posée sans l'appliquer à tous. Créez une liste dédiée pour
          négocier avec ce client.
        </div>
      )}
    </section>
  );
}

function Regles({ d, busy, onAdd, onDelete }: {
  d: ClientDetail; busy: string | null;
  onAdd: (b: RulePayload) => void;
  onDelete: (ruleId: number) => void;
}) {
  const bloque = !d.pricelist || d.pricelist.shared;
  return (
    <section style={{ marginTop: 18 }}>
      <h4 style={{ margin: '0 0 8px' }}>Remises et prix négociés</h4>
      {d.rules.length === 0
        ? (
          <div style={{ opacity: .7 }}>
            Aucune règle : ce client paie le prix du catalogue.
            {/* Sans cette phrase, la section n'offre rien et ressemble à une
                impasse : le bouton qui débloque est dans le bloc du dessus. */}
            {bloque && ' Pour poser une remise, créez-lui d\u2019abord une liste dédiée (bouton « + Liste dédiée à ce client » ci-dessus).'}
          </div>
        )
        : (
          <table>
            <thead><tr><th>Portée</th><th>Produit</th><th>Type</th><th className="num">Valeur</th><th></th></tr></thead>
            <tbody>
              {d.rules.map((r) => <RegleLigne key={r.id} r={r} bloque={bloque} busy={busy} onDelete={onDelete} />)}
            </tbody>
          </table>
        )}
      {!bloque && <AjoutRegle busy={busy} onAdd={onAdd} />}
    </section>
  );
}

function RegleLigne({ r, bloque, busy, onDelete }: {
  r: PriceRuleRow; bloque: boolean; busy: string | null; onDelete: (id: number) => void;
}) {
  const TYPE: Record<string, string> = {
    remise: 'Remise', majoration: 'Majoration (jamais affichée)', fixe: 'Prix imposé', autre: 'Règle Odoo',
  };
  return (
    <tr>
      <td>{r.scope === 'global' ? 'Tout le catalogue' : 'Un produit'}</td>
      <td>{r.target ?? '—'}</td>
      <td>{TYPE[r.kind]}</td>
      <td className="num">
        {r.kind === 'fixe' ? `${fmtMoney(r.price)}`
          : r.kind === 'autre' ? '—'
            : `${r.kind === 'majoration' ? '+' : '−'} ${r.percent.toLocaleString('fr-CH')} %`}
      </td>
      <td>
        {/* Une règle créée directement dans Odoo peut sortir des deux gestes de
            l'app (formule, cascade, marges) : on l'affiche, on ne la réécrit pas
            — et le PDF la laisse au prix catalogue plutôt que d'en inventer un. */}
        {r.unsupported
          ? <span style={{ opacity: .7 }}>créée dans Odoo, non modifiable ici</span>
          : !bloque && (
            <button className="devis-refresh" disabled={busy === 'regle'} onClick={() => onDelete(r.id)}>Supprimer</button>
          )}
      </td>
    </tr>
  );
}

function AjoutRegle({ busy, onAdd }: { busy: string | null; onAdd: (b: RulePayload) => void }) {
  const [scope, setScope] = useState<'global' | 'produit'>('global');
  const [kind, setKind] = useState<'remise' | 'majoration' | 'fixe'>('remise');
  const [valeur, setValeur] = useState('');
  const [q, setQ] = useState('');
  const [hits, setHits] = useState<CatalogueHit[]>([]);
  const [produit, setProduit] = useState<CatalogueHit | null>(null);

  useEffect(() => {
    if (scope !== 'produit' || q.trim().length < 2) { setHits([]); return; }
    const t = setTimeout(() => {
      api.searchCatalogue(q.trim()).then((r) => setHits(r.items)).catch(() => setHits([]));
    }, 300);
    return () => clearTimeout(t);
  }, [q, scope]);

  // Un prix imposé n'a de sens que sur un produit : le proposer « sur tout le
  // catalogue » mettrait le même montant sur 884 références.
  const kinds: [typeof kind, string][] = scope === 'produit'
    ? [['remise', 'Remise %'], ['majoration', 'Majoration %'], ['fixe', 'Prix imposé']]
    : [['remise', 'Remise %'], ['majoration', 'Majoration %']];
  const v = Number(valeur.replace(',', '.'));
  const ok = Number.isFinite(v) && v >= 0 && (scope === 'global' || produit != null)
    && (kind === 'fixe' || v <= 100);

  return (
    <div style={{ marginTop: 12, display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap' }}>
      <label className="form-field">
        <span>Portée</span>
        <select value={scope} onChange={(e) => {
          const s = e.target.value as typeof scope;
          setScope(s);
          if (s === 'global' && kind === 'fixe') setKind('remise');
        }}>
          <option value="global">Tout le catalogue</option>
          <option value="produit">Un produit</option>
        </select>
      </label>

      {scope === 'produit' && (
        <label className="form-field" style={{ minWidth: 260 }}>
          <span>Produit</span>
          {produit
            ? (
              <span>
                <b>{produit.code || '—'}</b> {produit.name} ({fmtMoney(produit.list_price)})
                <button className="link-btn" style={{ marginLeft: 8 }} onClick={() => { setProduit(null); setQ(''); }}>changer</button>
              </span>
            )
            : <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Code ou nom…" />}
          {!produit && hits.length > 0 && (
            <div className="devis-list" style={{ maxHeight: 160, overflow: 'auto', marginTop: 4 }}>
              {hits.map((h) => (
                <button key={h.id} className="link-btn" style={{ display: 'block', textAlign: 'left', padding: '3px 6px' }}
                  onClick={() => setProduit(h)}>
                  <b>{h.code || '—'}</b> {h.name} — {fmtMoney(h.list_price)}
                </button>
              ))}
            </div>
          )}
        </label>
      )}

      <label className="form-field">
        <span>Type</span>
        <select value={kind} onChange={(e) => setKind(e.target.value as typeof kind)}>
          {kinds.map(([k, lbl]) => <option key={k} value={k}>{lbl}</option>)}
        </select>
      </label>

      <label className="form-field" style={{ maxWidth: 120 }}>
        <span>{kind === 'fixe' ? 'Prix' : 'Pourcentage'}</span>
        <input value={valeur} onChange={(e) => setValeur(e.target.value)} placeholder={kind === 'fixe' ? '611' : '10'} />
      </label>

      <button
        className="devis-action"
        disabled={!ok || busy === 'regle'}
        onClick={() => {
          onAdd({
            scope, kind,
            ...(scope === 'produit' ? { product_tmpl_id: produit!.id } : {}),
            ...(kind === 'fixe' ? { price: v } : { percent: v }),
          });
          setValeur(''); setProduit(null); setQ('');
        }}
      >Ajouter</button>
    </div>
  );
}
